import React, { useState } from 'react';
import { Calendar, calendarUtils } from '../Calendar';
import { useCalendarEvents } from './useCalendarEvents';
import ActivityCalendarDay from './ActivityCalendarDay';
import { Loader } from '../Loader';
import Stack from '../Stack';
import { useIsMobileLayout } from '../../../hooks/useIsMobileLayout';
import { colors, shadows } from '../../../utils/theme';
import FocusedEventsPrompt from './FocusedEventsPrompt';
import { CalendarMode, EventType } from './types';
import ActivityCalendarHeader from './ActivityCalendarHeader';
import ActivityListContent from './ActivityListContent';
import YearScanMonthDisplay from './YearScanMonthDisplay';

export type ActivityCalendarOwnProps = {
    medicalProfileId: string;
};

export type ActivityCalendarProps = ActivityCalendarOwnProps;

const ActivityCalendar: React.FC<ActivityCalendarProps> = (props) => {
    const isMobile = useIsMobileLayout();
    const [monthYear, setMonthYear] = useState(calendarUtils.getMonthYearFromDate(new Date()));
    const { calendarEventsHelper, isLoading } = useCalendarEvents({
        medicalProfileId: props.medicalProfileId,
    });
    const [calendarMode, setCalendarMode] = useState<CalendarMode>('calendar');
    const [eventTypeFilter, setEventTypeFilter] = useState<EventType>('all');
    const [focusedIsoDate, setFocusedIsoDate] = useState<string | null>(null);

    return (
        <>
            <Calendar
                renderHeader={() => (
                    <ActivityCalendarHeader
                        calendarMode={calendarMode}
                        setCalendarMode={setCalendarMode}
                        eventType={eventTypeFilter}
                        setEventType={setEventTypeFilter}
                        profileId={props.medicalProfileId}
                    />
                )}
                style={[
                    isMobile
                        ? {
                              backgroundColor: colors.White,
                              borderRadius: 2,
                              borderWidth: 1,
                              borderColor: colors['Paper-Border'],
                              alignSelf: 'stretch',
                              ...shadows.card,
                          }
                        : {
                              alignSelf: 'stretch',
                          },
                ]}
                renderYearScanMonth={(monthYear) => (
                    <YearScanMonthDisplay
                        monthYear={monthYear}
                        calendarEventHelper={calendarEventsHelper}
                        eventTypeFilter={eventTypeFilter}
                    />
                )}
                monthYear={monthYear}
                onMonthYearChange={setMonthYear}
                renderDayContents={(args) => (
                    <ActivityCalendarDay
                        {...args}
                        calendarEventsHelper={calendarEventsHelper}
                        eventType={eventTypeFilter}
                    />
                )}
                getOnDayPressHandler={(calendarDay) =>
                    calendarEventsHelper.getHasEvents(calendarDay.isoDate, eventTypeFilter)
                        ? () => setFocusedIsoDate(calendarDay.isoDate)
                        : undefined
                }
                renderCalendarContent={
                    isLoading
                        ? ({ placeholderHeight }) => (
                              <Stack
                                  vertical
                                  style={{ width: '100%', height: placeholderHeight }}
                                  align="center"
                                  justify="center"
                              >
                                  <Loader />
                              </Stack>
                          )
                        : calendarMode === 'list'
                        ? ({ placeholderHeight }) => (
                              <Stack vertical style={{ width: '100%' }}>
                                  <ActivityListContent
                                      placeholderHeight={placeholderHeight}
                                      calendarEventHelper={calendarEventsHelper}
                                      monthYear={monthYear}
                                      eventType={eventTypeFilter}
                                      profileId={props.medicalProfileId}
                                  />
                              </Stack>
                          )
                        : undefined
                }
            />
            <FocusedEventsPrompt
                focusedIsoDate={focusedIsoDate}
                onClearFocusedIsoDate={() => setFocusedIsoDate(null)}
                calendarEventsHelper={calendarEventsHelper}
                profileId={props.medicalProfileId}
                eventType={eventTypeFilter}
            />
        </>
    );
};

export default React.memo(ActivityCalendar);
